import React from "react";
import { useTranslation } from "react-i18next";
import { Divider, Typography } from "@mui/material";
import { toCurrency } from "@helpers";
import {
  Investment,
  InvestmentPerformance,
  RealizedGainByBooking,
} from "@bn/api/apiv1";
import { DataBlockLayout } from "@components";
import { Content } from "./components";
import { extractGainLossValues } from "./helpers";
import { EmptyGainLossStatisticsBlock } from "./EmptyGainLossStatisticsBlock";
import {
  ReturnOnInvestmentAccordion,
  RealizedGainsAccordion,
  PerformanceAccordion,
} from "./accordions";

interface Props {
  investment: Investment;
  realizedGainByBooking: RealizedGainByBooking;
  investmentPerformance: InvestmentPerformance;
}

const GainLossStatisticsBlock = ({
  investment,
  realizedGainByBooking,
  investmentPerformance,
  currency,
}: Props & { currency: string }) => {
  const { t } = useTranslation();
  const labelColor = "textSecondary";
  const textVariant = "h6";

  const isForeignCurrencyInvestment =
    investment?.currency !== investment?.account?.currency;

  const values = extractGainLossValues(
    investment,
    realizedGainByBooking,
    investmentPerformance
  );
  const totalGain = values.netTotalGain + values.returnOnInvestment;
  const totalColor =
    totalGain > 0
      ? "text.positive"
      : totalGain < 0
        ? "text.negative"
        : "textPrimary";

  return (
    <Content>
      <Typography color={labelColor} variant="body4">
        {t("investment.details.gainLossStatistics.gainAndLoss")}
      </Typography>
      <Typography
        variant={textVariant}
        sx={{ color: totalColor }}
        data-testid="gain-and-loss"
      >
        {toCurrency(totalGain, currency)}
      </Typography>
      <Divider sx={{ mt: 3, mb: 4 }} />
      <ReturnOnInvestmentAccordion
        investment={investment}
        values={values}
        currency={currency}
      />
      <Divider sx={{ mt: 3, mb: 4 }} />
      <RealizedGainsAccordion values={values} currency={currency} />
      <Divider sx={{ mt: 3, mb: 4 }} />
      <PerformanceAccordion
        values={values}
        investmentPerformance={investmentPerformance}
        isForeignCurrencyInvestment={isForeignCurrencyInvestment}
      />
    </Content>
  );
};

export const GainLossStatistics = ({
  isFetching,
  investment,
  realizedGainByBooking,
  investmentPerformance,
  currency,
}: Partial<Props> & {
  isFetching: boolean;
  currency?: string;
}) => {
  const { t } = useTranslation();

  return (
    <DataBlockLayout
      title={t("investment.details.gainLossStatistics.title")}
      isFetching={isFetching}
    >
      {investment &&
      realizedGainByBooking &&
      investmentPerformance &&
      currency ? (
        <GainLossStatisticsBlock
          investment={investment}
          realizedGainByBooking={realizedGainByBooking}
          investmentPerformance={investmentPerformance}
          currency={currency}
        />
      ) : (
        <EmptyGainLossStatisticsBlock />
      )}
    </DataBlockLayout>
  );
};
